/**
 * ErectorEndorsements — CR-2026-ERECTOR-E3 (2026-10-13). AISC 207-25 §5.3.1 Seismic,
 * §5.3.2 Metal Deck, §5.3.3 Bridge erection endorsements: evidence register.
 * Server: src/routes/erectorEndorsements.js. Evidence is never edited or deleted;
 * QC review is once, by someone other than the recorder; a wrong item is voided.
 * SFM never says an endorsement is held: AISC decides that.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert, MenuItem, TextField, Grid,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import PageGuide from "../../components/PageGuide";
import ApiCall from "../../utils/apicall";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import { apiMessage } from "../../utils/erectionPlansApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const ENDORSEMENTS = { SEISMIC: "Seismic (§5.3.1)", METAL_DECK: "Metal deck (§5.3.2)", BRIDGE: "Bridge (§5.3.3)" };
const EVIDENCE = {
  WRITTEN_PROCEDURE: "Written procedure", TRAINING_RECORD: "Training record", PERSONNEL_QUALIFICATION: "Personnel qualification",
  PROJECT_EXPERIENCE: "Project experience", DEMONSTRATION_RECORD: "Demonstration record", INTERNAL_AUDIT: "Internal audit", OTHER: "Other",
};
// Local calendar date (not UTC): after about 8 pm Eastern, UTC is already tomorrow.
const today = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const empty = () => ({ endorsement: "SEISMIC", evidence_type: "WRITTEN_PROCEDURE", title: "", reference: "", evidence_date: today(), job_id: "", notes: "" });

function reviewChip(r) {
  if (r.voided) return <Chip size="small" label={`Voided: ${r.void_reason}`} />;
  if (r.review_result === "ACCEPTED") return <Chip size="small" color="success" label="QC accepted" />;
  if (r.review_result === "RETURNED") return <Chip size="small" color="error" label={`Returned: ${r.review_note}`} />;
  return <Chip size="small" color="warning" label="Awaiting QC review" />;
}

export default function ErectorEndorsements() {
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: perms = [], id: myId } = loginUser?.decodedToken ?? {};
  const canRecord = hasPermission(perms, PERMISSIONS.RECORD_ENDORSEMENT_EVIDENCE);
  const canReview = hasPermission(perms, PERMISSIONS.REVIEW_ENDORSEMENT_EVIDENCE);

  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("");
  const [summary, setSummary] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ApiCall.get("/job").then((r) => setJobs(r?.data?.data?.jobs || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        ApiCall.get("/erector-endorsements/summary"),
        ApiCall.get(`/erector-endorsements/evidence${filter ? `?endorsement=${filter}` : ""}`),
      ]);
      setSummary(s?.data?.data?.summary || []);
      setDisclaimer(s?.data?.data?.disclaimer || "");
      setRows(l?.data?.data?.evidence || []);
    } catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Could not load endorsement evidence.") }); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit() {
    setBusy(true); setMsg(null);
    try {
      await ApiCall.post("/erector-endorsements/evidence", { ...form, job_id: form.job_id ? Number(form.job_id) : undefined });
      setMsg({ severity: "success", text: "Evidence recorded. It now needs QC review by another person." });
      setForm((f) => ({ ...empty(), endorsement: f.endorsement }));
      await load();
    } catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Evidence not recorded.") }); }
    finally { setBusy(false); }
  }

  async function review(r, result) {
    let note = "";
    if (result === "RETURNED") {
      // eslint-disable-next-line no-alert
      note = window.prompt("Return this evidence. Say what is missing or wrong:") || "";
      if (!note.trim()) return;
    }
    try { await ApiCall.post(`/erector-endorsements/evidence/${r.id}/review`, { result, note }); await load(); }
    catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Review not saved.") }); }
  }

  async function voidItem(r) {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(`Void "${r.title}"? Give the reason:`);
    if (!reason || !reason.trim()) return;
    try { await ApiCall.post(`/erector-endorsements/evidence/${r.id}/void`, { reason }); await load(); }
    catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Not voided.") }); }
  }

  return (
    <>
      <Helmet><title>Erector Endorsements | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>Erector Endorsements</Typography>
        <PageGuide
          why="A certified erector seeking or holding the Seismic, Metal Deck or Bridge endorsement keeps evidence that the auditor will ask to see."
          who="QM, PM or Admin record evidence. A different QM or Admin reviews each item."
          what="An append-only evidence register per endorsement, with QC review. It does not say whether an endorsement is granted."
          how="Pick the endorsement and evidence type, give a title, reference and date, and link a job when the evidence comes from one."
          when="When a procedure is issued or revised, after training, and after a demonstration project."
          standard="AISC 207-25 §5.3.1 · §5.3.2 · §5.3.3"
        />
        {disclaimer && <Alert severity="info" sx={{ mb: 2 }}>{disclaimer}</Alert>}
        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }}>{msg.text}</Alert>}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {summary.map((s) => (
            <Grid item xs={12} md={4} key={s.endorsement}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{ENDORSEMENTS[s.endorsement]}</Typography>
                <Typography variant="body2">On file: {s.on_file} · QC accepted: {s.accepted} · Returned: {s.returned} · Awaiting review: {s.awaiting_review}</Typography>
                {s.voided > 0 && <Typography variant="caption" color="text.secondary">Voided: {s.voided}</Typography>}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {canRecord && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Record evidence</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
              <TextField select size="small" label="Endorsement" value={form.endorsement} onChange={set("endorsement")} sx={{ minWidth: 200 }}>
                {Object.entries(ENDORSEMENTS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Evidence type" value={form.evidence_type} onChange={set("evidence_type")} sx={{ minWidth: 220 }}>
                {Object.entries(EVIDENCE).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Title *" value={form.title} onChange={set("title")} sx={{ minWidth: 260 }} />
              <TextField size="small" label="Reference (doc no. / rev)" value={form.reference} onChange={set("reference")} />
              <TextField size="small" type="date" label="Date *" InputLabelProps={{ shrink: true }} value={form.evidence_date} onChange={set("evidence_date")} />
              <TextField select size="small" label="Job (optional)" value={form.job_id} onChange={set("job_id")} sx={{ minWidth: 200 }}>
                <MenuItem value="">None</MenuItem>
                {jobs.map((j) => <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField fullWidth size="small" label={form.evidence_type === "OTHER" ? "Notes * (describe the evidence)" : "Notes"} value={form.notes} onChange={set("notes")} sx={{ mb: 1 }} />
            <Button variant="contained" disabled={busy || !form.title.trim() || !form.evidence_date || (form.evidence_type === "OTHER" && !form.notes.trim())} onClick={submit}>Record</Button>
          </Paper>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField select size="small" label="Show" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 240 }}>
            <MenuItem value="">All endorsements</MenuItem>
            {Object.entries(ENDORSEMENTS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
        </Paper>

        <TableContainer component={Paper}>
          <SmartTable tableId="erector-endorsements" lockedColumns={["Date", "Endorsement", "QC review"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Endorsement</TableCell><TableCell>Evidence</TableCell><TableCell>Title</TableCell>
                <TableCell>Reference</TableCell><TableCell>QC review</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={7}>No evidence recorded yet.</TableCell></TableRow>}
              {rows.map((r) => {
                const pending = !r.voided && !r.review_result;
                const mine = Number(r.recorded_by) === Number(myId);
                return (
                  <TableRow key={r.id} sx={r.voided ? { opacity: 0.5 } : undefined}>
                    <TableCell>{String(r.evidence_date).slice(0, 10)}</TableCell>
                    <TableCell>{ENDORSEMENTS[r.endorsement]}</TableCell>
                    <TableCell>{EVIDENCE[r.evidence_type]}</TableCell>
                    <TableCell>{r.title}{r.notes ? <Typography variant="caption" display="block" color="text.secondary">{r.notes}</Typography> : null}</TableCell>
                    <TableCell>{r.reference || "—"}</TableCell>
                    <TableCell>{reviewChip(r)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {canReview && pending && !mine && <Button size="small" onClick={() => review(r, "ACCEPTED")}>Accept</Button>}
                        {canReview && pending && !mine && <Button size="small" color="error" onClick={() => review(r, "RETURNED")}>Return</Button>}
                        {canReview && pending && mine && <Typography variant="caption" color="text.secondary">Another reviewer needed</Typography>}
                        {canRecord && !r.voided && <Button size="small" onClick={() => voidItem(r)}>Void</Button>}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>
    </>
  );
}
